import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { motion } from "framer-motion";

const LOGO =
  "https://customer-assets.emergentagent.com/job_heritage-blend-4/artifacts/wbbvy8m1_ER%20Logo%202.png";
const LOGO_MARK =
  "https://customer-assets.emergentagent.com/job_heritage-blend-4/artifacts/utpozekc_ER%20Logo.png";

const sections = [
  { key: "who_we_are", id: "heritage" },
  { key: "land", id: "land" },
  { key: "seed_to_smoke", id: "journey" },
  { key: "master_blender", id: "craft" },
  { key: "final_cigar", id: "collection" },
  { key: "archive", id: "archive" },
  { key: "work_with_us", id: "work" },
];

export default function NavBar() {
  const { t, lang, setLanguage } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.4, delay: 0.4, ease: [0.6, 0.01, 0.05, 0.95] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-pearl/90 backdrop-blur-md border-b border-tobacco/15 shadow-[0_24px_48px_-32px_rgba(75,46,31,0.18)]"
          : "bg-transparent"
      }`}
    >
      <div
        className={`max-w-[1700px] mx-auto px-6 md:px-12 flex items-center justify-between gap-8 transition-all duration-700 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <Link
          to="/"
          className="group flex items-center gap-3 relative"
          data-testid="navbar-brand"
          aria-label="Elias Ramirez Tabaco Company — Home"
        >
          {/* Subtle gold halo on hover */}
          <span className="absolute -inset-2 rounded-full bg-gold/0 group-hover:bg-gold/10 blur-md transition-all duration-700 pointer-events-none" />
          <motion.img
            src={LOGO}
            alt="Elias Ramirez Tabaco Company"
            className={`relative block w-auto transition-all duration-700 ${
              scrolled ? "h-10" : "h-12 md:h-14"
            }`}
            style={{ filter: "drop-shadow(0 2px 6px rgba(75,46,31,0.18))" }}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.6, ease: [0.65, 0, 0.35, 1] }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 font-mono text-[10.5px] xl:text-[11px] font-medium tracking-widest2 uppercase text-tobacco/85">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => goTo(s.id)}
              data-testid={`nav-${s.id}`}
              className="relative hover:text-tobacco transition-colors duration-500 group"
            >
              {t(`nav.${s.key}`)}
              <span className="absolute left-0 right-0 -bottom-1 h-px bg-tobacco scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-700" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="font-mono text-[11px] font-medium tracking-widest2 uppercase flex items-center gap-2 select-none"
            data-testid="lang-toggle"
          >
            <button
              onClick={() => setLanguage("en")}
              data-testid="lang-en"
              className={`transition-colors duration-500 ${
                lang === "en" ? "text-tobacco" : "text-tobacco/55 hover:text-tobacco/85"
              }`}
            >
              EN
            </button>
            <span className="text-tobacco/30">/</span>
            <button
              onClick={() => setLanguage("es")}
              data-testid="lang-es"
              className={`transition-colors duration-500 ${
                lang === "es" ? "text-tobacco" : "text-tobacco/55 hover:text-tobacco/85"
              }`}
            >
              ES
            </button>
          </div>
          <button
            className="lg:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <span
              className={`w-5 h-px bg-tobacco transition-transform duration-500 ${
                open ? "translate-y-1.5 rotate-45" : ""
              }`}
            />
            <span className={`w-5 h-px bg-tobacco transition-opacity duration-500 ${open ? "opacity-0" : ""}`} />
            <span
              className={`w-5 h-px bg-tobacco transition-transform duration-500 ${
                open ? "-translate-y-1.5 -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.7, ease: [0.65, 0, 0.35, 1] }}
        className="lg:hidden bg-pearl border-t border-tobacco/15 overflow-hidden"
      >
        <div className="px-6 py-8 grid gap-5 font-mono text-[11px] tracking-widest2 uppercase text-tobacco/85">
          <img src={LOGO_MARK} alt="" className="h-10 w-auto opacity-90 mb-2" />
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => goTo(s.id)}
              data-testid={`mobile-nav-${s.id}`}
              className="text-left hover:text-tobacco transition-colors"
            >
              {t(`nav.${s.key}`)}
            </button>
          ))}
        </div>
      </motion.div>
    </motion.header>
  );
}
